import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, ROLES } from "@/lib/constants";
import { coverUrl } from "@/lib/training";
import { PageHeader } from "@/components/Badges";
import { LessonEditor } from "@/components/course/LessonEditor";
import { AudienceField } from "@/components/AudienceField";
import { ExamInsights } from "@/components/course/ExamInsights";
import { QuizEditor } from "@/components/course/QuizEditor";
import { FileUpload } from "@/components/course/FileUpload";
import { LinkedFormsField } from "@/components/forms/RelatedForms";

const EMPTY = { title: "", description: "", category: "", cover_file_id: null, cover_image_url: null, applicable_roles: [], estimated_minutes: 30, linked_sop_ids: [], audience_region_ids: [], lessons: [], quiz: { pass_mark: 80, questions: [], per_attempt: null, shuffle_questions: true, shuffle_options: true } };

const Section = ({ title, children, testId }) => (
  <section className="rounded-3xl border border-lasso-line bg-white/60 p-6 sm:p-8" data-testid={testId}>
    <h2 className="mb-6 font-display text-sm font-bold uppercase tracking-[0.14em]">{title}</h2>
    {children}
  </section>
);

export default function CourseBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [sops, setSops] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/sops", { params: { status: "published" } }).then((r) => setSops(r.data));
    if (!id) return;
    api.get(`/admin/courses/${id}`).then(({ data }) => {
      setOriginal(data);
      const { id: _i, slug, status, version, created_at, updated_at, created_by, updated_by, published_at, ...rest } = data;
      setForm(rest);
    });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);
  const moveLesson = (i, d) => {
    const ls = [...form.lessons];
    [ls[i], ls[i + d]] = [ls[i + d], ls[i]];
    set("lessons", ls);
  };
  const addLesson = (type) => set("lessons", [...form.lessons, { type, title: "", content: "", minutes: 5, video_url: null, file_id: null }]);

  const save = async (publish) => {
    if (!form.title.trim() || !form.category) return toast.error("Title and category are required");
    if (!form.applicable_roles.length) return toast.error("Choose at least one role");
    const questions = [];
    for (const q of form.quiz.questions) {
      const label = q.prompt?.trim() || "a question";
      if (q.type === "tf") {
        questions.push(q);
        continue;
      }
      const kept = q.options.map((o, i) => ({ o: o.trim(), i })).filter((x) => x.o);
      if (kept.length < 2) return toast.error(`"${label}" needs at least 2 non-blank options`);
      const ci = kept.findIndex((x) => x.i === q.correct_index);
      if (ci < 0) return toast.error(`Pick a correct (non-blank) answer for "${label}"`);
      questions.push({ ...q, options: kept.map((x) => x.o), correct_index: ci });
    }
    setSaving(true);
    try {
      const payload = { ...form, quiz: { ...form.quiz, questions }, status: publish ? "published" : id ? null : "draft" };
      const { data } = id ? await api.put(`/courses/${id}`, payload) : await api.post("/courses", payload);
      toast.success(publish ? "Course published" : "Course saved");
      navigate(`/courses/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const isPublished = original?.status === "published";
  const [tab, setTab] = useState("build");
  useEffect(() => {
    const h = window.location.hash;
    if (h.startsWith("#q-") && original) setTimeout(() => document.getElementById(h.slice(1))?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [original]);
  const lessonTypes = [["text", "Text"], ["video", "Video"], ["pdf", "PDF"]];

  return (
    <div className="fade-up">
      <Link to={id ? `/courses/${id}` : "/courses"} className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="builder-back"><ArrowLeft className="h-4 w-4" /> Back</Link>
      <div className="mt-6"><PageHeader eyebrow={id ? `Course builder · v${original?.version ?? ""}` : "Course builder"} title={form.title || (id ? "Edit course" : "New course")} /></div>
      {id && (
        <div className="mb-8 inline-flex rounded-full border border-lasso-line bg-white p-1" data-testid="builder-tabs">
          {[["build", "Build"], ["insights", "Exam insights"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-sm font-semibold ${tab === k ? "bg-lasso-ink text-white" : "text-zinc-600"}`} data-testid={`builder-tab-${k}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "insights" ? <ExamInsights courseId={id} /> : <>
      {isPublished && (
        <div className="mb-8 flex gap-3 rounded-xl border border-lasso-sky bg-lasso-tint p-4 text-sm text-lasso-navy">
          <Info className="mt-0.5 h-4 w-4 shrink-0" /> This course is live. Learners who already completed it keep their completion and certificate.
        </div>
      )}
      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-8">
          <Section title="Details" testId="builder-details">
            <div className="space-y-5">
              <div className="space-y-2"><Label>Title</Label><Input className="h-11" value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="builder-title-input" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="builder-description-input" /></div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={form.category || undefined} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="builder-category-select"><SelectValue placeholder="Choose category" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value} data-testid={`builder-category-${c.value.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c.value}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Estimated minutes</Label><Input type="number" min={1} value={form.estimated_minutes} onChange={(e) => set("estimated_minutes", Number(e.target.value) || 1)} data-testid="builder-minutes-input" /></div>
                <div className="space-y-2">
                  <Label>Certificate validity</Label>
                  <Select value={form.validity_months ? String(form.validity_months) : "never"} onValueChange={(v) => set("validity_months", v === "never" ? null : Number(v))}>
                    <SelectTrigger data-testid="builder-validity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never expires</SelectItem>
                      {[6, 12, 24, 36].map((n) => <SelectItem key={n} value={String(n)} data-testid={`builder-validity-${n}`}>{n} months</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cover image</Label>
                <div className="flex items-center gap-4">
                  {coverUrl(form) && <img src={coverUrl(form)} alt="" className="h-16 w-28 rounded-lg object-cover" data-testid="builder-cover-preview" />}
                  <FileUpload accept="image/*" label={coverUrl(form) ? "Replace cover" : "Upload cover"} testId="builder-cover-upload" onUploaded={(f) => setForm((x) => ({ ...x, cover_file_id: f.id, cover_image_url: null }))} />
                </div>
              </div>
            </div>
          </Section>

          <Section title={`Lessons (${form.lessons.length})`} testId="builder-lessons">
            <div className="space-y-3">
              {form.lessons.map((l, i) => (
                <LessonEditor key={l.id || `new-${i}`} lesson={l} index={i} total={form.lessons.length} onChange={(v) => set("lessons", form.lessons.map((x, j) => (j === i ? v : x)))} onMove={(d) => moveLesson(i, d)} onRemove={() => set("lessons", form.lessons.filter((_, j) => j !== i))} />
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {lessonTypes.map(([t, label]) => (
                <button key={t} type="button" onClick={() => addLesson(t)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-lasso-line px-4 py-2 text-xs font-semibold hover:border-lasso-ink" data-testid={`builder-add-${t}-lesson`}>
                  <Plus className="h-3.5 w-3.5" /> {label} lesson
                </button>
              ))}
            </div>
          </Section>

          <Section title={`Exam · question bank (${form.quiz.questions.length})`} testId="builder-quiz">
            <QuizEditor quiz={form.quiz} onChange={(q) => set("quiz", q)} />
          </Section>
        </div>

        <aside className="space-y-6 lg:sticky lg:top-10 lg:self-start">
          <LinkedFormsField kind="courses" id={id} />
          <div className="space-y-5 rounded-2xl border border-lasso-line bg-white p-5">
            <AudienceField value={form.audience_region_ids || []} onChange={(v) => setForm((f) => ({ ...f, audience_region_ids: v }))} testId="builder-audience" />
            <div className="space-y-3">
              <Label>Applicable roles</Label>
              {ROLES.map((r) => (
                <label key={r.value} className="flex cursor-pointer items-center gap-3 text-sm">
                  <Checkbox checked={form.applicable_roles.includes(r.value)} onCheckedChange={() => toggle("applicable_roles", r.value)} data-testid={`builder-role-${r.value}`} /> {r.label}
                </label>
              ))}
            </div>
            <div className="space-y-3">
              <Label>Linked SOPs</Label>
              <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                {sops.map((s) => (
                  <label key={s.id} className="flex cursor-pointer items-start gap-3 text-xs">
                    <Checkbox className="mt-0.5" checked={form.linked_sop_ids.includes(s.id)} onCheckedChange={() => toggle("linked_sop_ids", s.id)} data-testid={`builder-sop-${s.id}`} /> {s.title}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button disabled={saving} onClick={() => save(true)} className="flex items-center justify-center gap-2 rounded-full bg-lasso-ink px-5 py-3 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="builder-publish-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {isPublished ? "Save changes" : "Save & publish"}
            </button>
            {!isPublished && (
              <button disabled={saving} onClick={() => save(false)} className="rounded-full border border-lasso-line bg-white px-5 py-3 text-sm font-semibold hover:border-lasso-ink disabled:opacity-60" data-testid="builder-save-draft-button">Save as draft</button>
            )}
          </div>
        </aside>
      </div>
      </>}
    </div>
  );
}
